import type { RevitFamily } from '../../../api/revit/types';
import { buildConnectorParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import {
  type AvailableProjectParameterDescriptor,
  buildTolerantProjectParameterMutationBatches,
  type ParameterBindingKind,
} from '../../revit/projectParameterMutationPlan';
import {
  type FamilyParameterMutationTargetSelection,
  getFamilyParameterBindingKind,
  normalizeFamilyCategoryKey,
  selectFamilyParameterMutationTargets,
} from '../../revit/familyParameterTargetSelection';
import { countUndoPlanEntries, countUndoPlanFamilies } from '../../parameterUndo/undoPlanCounts';
import {
  isNativeRevitConnectorParameterName,
  normalizeRevitParameterName,
  sanitizeRevitParameterNames,
} from '../../revitParameterNames';
import { type ConnectorParameterUndoEntry, type ConnectorParameterUndoPlan } from '../../../stores/useUIStore';
import type { ConnectorBindingConfiguration, MenuConfiguration } from '../../../types';
import { getEquivalentParameterLookupNames } from './availableProjectParameterCache';
import {
  CONNECTOR_PARAMETER_CATEGORIES,
  EDITABLE_FAMILY_CONNECTOR_CATEGORIES,
  PIPE_SYSTEM_CONNECTOR_CATEGORIES,
} from './connectorCategories';
import { familyHasParameter } from './familyParameterAvailability';
import { pushFormFieldValue } from './formFieldValues';
import { type ConnectorAdditionOperation, type ParameterAdditionMethod } from './models';
import { areProjectParametersAvailable } from './parameterNameAvailability';

export {
  CONNECTOR_PARAMETER_CATEGORIES,
  EDITABLE_FAMILY_CONNECTOR_CATEGORIES,
  PIPE_SYSTEM_CONNECTOR_CATEGORIES,
} from './connectorCategories';

export type ConnectorFamilyParameterTargetSelection = FamilyParameterMutationTargetSelection;

export function selectConnectorFamilyParameterTargets(
  operation: ConnectorAdditionOperation,
  families: RevitFamily[],
  snapshotAvailable = families.length > 0,
): ConnectorFamilyParameterTargetSelection {
  if (operation.destination !== 'family') {
    return {
      hasReliableSnapshot: true,
      matchingFamilyCount: 0,
      compatibleFamilyCount: 0,
      targetFamilyIds: [],
      matchingFamilies: [],
      compatibleFamilies: [],
      targetFamilies: [],
    };
  }

  if (families.length === 0) {
    return {
      hasReliableSnapshot: snapshotAvailable,
      matchingFamilyCount: 0,
      compatibleFamilyCount: 0,
      targetFamilyIds: [],
      matchingFamilies: [],
      compatibleFamilies: [],
      targetFamilies: [],
    };
  }

  const targetCategories = new Set(
    operation.categories.map(normalizeFamilyCategoryKey).filter(Boolean),
  );

  return selectFamilyParameterMutationTargets(
    families,
    operation.parameterNames,
    operation.bindingKind,
    (family) =>
      targetCategories.size === 0 ||
      targetCategories.has(normalizeFamilyCategoryKey(family.category)),
    operation.parameterTypeHints ?? [],
  );
}

export function collectConnectorFamilyMutationTargetIds(
  selection: ConnectorFamilyParameterTargetSelection,
  previouslyKnownFamilyKeys: Iterable<string> = [],
): string[] {
  if (!selection.hasReliableSnapshot) {
    return [];
  }

  // The host snapshot carries one representative placed instance per family
  // symbol, so compatible instance-bound definitions can be skipped without a
  // family-document preflight. Unplaced or incomplete families remain targets.
  const knownFamilyKeys = new Set(
    Array.from(
      previouslyKnownFamilyKeys,
      (familyKey) => familyKey.trim().toLowerCase(),
    )
      .filter(Boolean),
  );
  return Array.from(
    new Set(
      selection.targetFamilies
        .filter((family) => !knownFamilyKeys.has(buildConnectorFamilyIdentityKey(family)))
        .map((family) => family.id.trim())
        .filter(Boolean),
    ),
  );
}

export function buildConnectorFamilyIdentityKey(
  family: Pick<RevitFamily, 'name' | 'category'>,
): string {
  return `${normalizeFamilyCategoryKey(family.category)}::${family.name.trim()}`
    .toLowerCase();
}

export function buildConnectorUndoPlan(
  families: RevitFamily[],
  parameterNames: string[],
  targetCategories: string[],
  projectParameterNames: string[] = [],
): ConnectorParameterUndoPlan | null {
  const normalizedCategories = new Set(
    targetCategories.map(normalizeFamilyCategoryKey),
  );
  const targetFamilies = families.filter((family) => {
    if (!family?.id || !family?.name) {
      return false;
    }

    if (family.isEditable === false || family.isSystemFamily) {
      return false;
    }

    return normalizedCategories.has(
      normalizeFamilyCategoryKey(family.category),
    );
  });

  const entries = targetFamilies.flatMap((family) =>
    parameterNames.map<ConnectorParameterUndoEntry>((parameterName) => {
      const previousBindingKind = getFamilyParameterBindingKind(
        family,
        parameterName,
      );
      return {
        familyId: family.id,
        familyName: family.name,
        parameterName,
        existedBefore: previousBindingKind !== null,
        ...(previousBindingKind ? { previousBindingKind } : {}),
      };
    })
  );

  if (entries.length === 0 && projectParameterNames.length === 0) {
    return null;
  }

  // Counted from the entries, never from the families the filter considered:
  // the stored counts are read back as a promise of what the undo will send.
  return {
    id: `connector-undo-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    createdAt: new Date().toISOString(),
    projectKey: '',
    projectParameterNames,
    familyCount: countUndoPlanFamilies({ entries }),
    entryCount: countUndoPlanEntries({ entries }),
    status: 'available',
    entries,
  };
}

export function areConnectorParametersAlreadyAvailable(
  params: string[],
  method: ParameterAdditionMethod,
  availableProjectParameters: string[],
  families: any[],
): boolean {
  if (params.length === 0) {
    return true;
  }

  if (method === 'project') {
    return areProjectParametersAvailable(params, availableProjectParameters);
  }

  const relevantLoadableFamilies = families.filter((family: any) => {
    const isPipeCategory = PIPE_CATEGORIES.some((category) =>
      family.category?.toLowerCase().includes(category.toLowerCase())
    );
    return isPipeCategory && family.isSystemFamily !== true &&
      family.isEditable !== false;
  });

  const relevantSystemFamilies = families.filter((family: any) => {
    const isPipeCategory = PIPE_CATEGORIES.some((category) =>
      family.category?.toLowerCase().includes(category.toLowerCase())
    );
    return isPipeCategory && family.isSystemFamily === true;
  });

  const loadableSatisfied = relevantLoadableFamilies.length === 0 ||
    relevantLoadableFamilies.every((family) => params.every((paramName) => familyHasParameter(family, paramName)));
  const systemSatisfied = relevantSystemFamilies.length === 0 ||
    areProjectParametersAvailable(params, availableProjectParameters);

  return loadableSatisfied && systemSatisfied;
}

export function collectPipeConnectorBindingParameters(config: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
}): string[] {
  const { menuConfig, connectorConfig } = config;
  const parameterNames: string[] = [];

  if (menuConfig?.sKeyValue) parameterNames.push(menuConfig.sKeyValue);
  if (menuConfig?.objectTypeValue) {
    parameterNames.push(menuConfig.objectTypeValue);
  }
  if (menuConfig?.pipingSpecValue) {
    parameterNames.push(menuConfig.pipingSpecValue);
  }
  if (menuConfig?.systemGroupingValue) {
    parameterNames.push(menuConfig.systemGroupingValue);
  }

  pushFormFieldValue(parameterNames, connectorConfig?.endpoint1Value);
  pushFormFieldValue(parameterNames, connectorConfig?.endpoint2Value);
  pushFormFieldValue(parameterNames, connectorConfig?.endpoint3Value);
  pushFormFieldValue(parameterNames, connectorConfig?.endpoint4Value);
  pushFormFieldValue(parameterNames, connectorConfig?.materialValue);
  pushFormFieldValue(parameterNames, connectorConfig?.designStdValue);
  pushFormFieldValue(parameterNames, connectorConfig?.pressureClassValue);
  pushFormFieldValue(parameterNames, connectorConfig?.insulationSpecValue);
  pushFormFieldValue(parameterNames, connectorConfig?.insulationThicknessValue);
  pushFormFieldValue(parameterNames, connectorConfig?.tracingSpecValue);
  pushFormFieldValue(parameterNames, connectorConfig?.itemDescriptionValue);

  return sanitizeRevitParameterNames(parameterNames);
}

export function collectNonPipeConnectorBindingParameters(config: {
  menuConfig: MenuConfiguration;
}): string[] {
  return sanitizeRevitParameterNames([config.menuConfig?.objectTypeValue]);
}

export function normalizeConnectorAdditionParameterNames(
  parameterNames: string[],
): string[] {
  return sanitizeRevitParameterNames(
    parameterNames.map(normalizeRevitParameterName),
  );
}

export function buildConnectorAdditionOperations(config: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
}): ConnectorAdditionOperation[] {
  const connectorParameterNames = normalizeConnectorAdditionParameterNames(
    collectConnectorBindingParameters(config),
  );
  if (connectorParameterNames.length === 0) {
    return [];
  }

  const parameterTypeHints = buildConnectorParameterTypeHints(config);

  if (config.connectorConfig.parameterDestination === 'family') {
    const projectOperation: ConnectorAdditionOperation = {
      scope: 'pipeSystem',
      destination: 'project',
      bindingKind: config.connectorConfig.parameterBindingKind,
      categories: PIPE_SYSTEM_CONNECTOR_CATEGORIES,
      parameterNames: connectorParameterNames,
      parameterTypeHints,
    };
    const familyParameterNames = connectorParameterNames.filter(
      (parameterName) => !isNativeRevitConnectorParameterName(parameterName),
    );
    if (familyParameterNames.length === 0) {
      return [projectOperation];
    }

    const familyParameterLookup = new Set(
      familyParameterNames.map((parameterName) => parameterName.toLowerCase()),
    );
    return [
      projectOperation,
      {
        scope: 'editableFamilies',
        destination: 'family',
        bindingKind: config.connectorConfig.parameterBindingKind,
        categories: EDITABLE_FAMILY_CONNECTOR_CATEGORIES,
        parameterNames: familyParameterNames,
        parameterTypeHints: parameterTypeHints.filter((hint) =>
          familyParameterLookup.has(hint.name.trim().toLowerCase())
        ),
      },
    ];
  }

  return [
    {
      scope: 'all',
      destination: config.connectorConfig.parameterDestination,
      bindingKind: config.connectorConfig.parameterBindingKind,
      categories: CONNECTOR_PARAMETER_CATEGORIES,
      parameterNames: connectorParameterNames,
      parameterTypeHints,
    },
  ];
}

export function withConnectorAdditionTarget(
  connectorConfig: ConnectorBindingConfiguration,
  target: Pick<ConnectorBindingConfiguration, 'parameterDestination' | 'parameterBindingKind'>,
): ConnectorBindingConfiguration {
  return { ...connectorConfig, ...target };
}

export function collectConnectorReloadParameterNames(
  operations: ConnectorAdditionOperation[],
): string[] {
  return sanitizeRevitParameterNames(
    operations.flatMap((operation) => operation.parameterNames),
  );
}

export function collectConnectorUndoReloadParameterNames(
  plan: ConnectorParameterUndoPlan,
): string[] {
  return sanitizeRevitParameterNames([
    ...plan.projectParameterNames,
    ...plan.entries.map((entry) => entry.parameterName),
  ]);
}

export function createAvailableProjectParameterTypeMap(
  availableParameters: AvailableProjectParameterDescriptor[],
): Map<string, Set<'Type' | 'Instance'>> {
  const map = new Map<string, Set<'Type' | 'Instance'>>();

  availableParameters.forEach((parameter) => {
    getEquivalentParameterLookupNames(parameter.name).forEach(
      (normalizedName) => {
        if (!map.has(normalizedName)) {
          map.set(normalizedName, new Set());
        }
        map.get(normalizedName)?.add(parameter.parameterType);
      },
    );
  });

  return map;
}

export function areProjectParametersAvailableForBinding(
  requestedParameters: string[],
  bindingKind: ParameterBindingKind,
  availableParameters: AvailableProjectParameterDescriptor[],
): boolean {
  const requiredParameterType = bindingKind === 'type' ? 'Type' : 'Instance';
  const availableParameterTypeMap = createAvailableProjectParameterTypeMap(
    availableParameters,
  );

  return sanitizeRevitParameterNames(requestedParameters).every(
    (parameterName) => {
      const parameterTypes = availableParameterTypeMap.get(
        parameterName.trim().toLowerCase(),
      );
      return parameterTypes?.has(requiredParameterType) ?? false;
    },
  );
}

export function areConnectorAdditionOperationsAvailable(
  operations: ConnectorAdditionOperation[],
  availableParameters: AvailableProjectParameterDescriptor[],
): boolean {
  if (operations.length === 0) {
    return true;
  }

  return operations.every((operation) => {
    if (operation.destination === 'family') {
      return false;
    }

    return buildTolerantProjectParameterMutationBatches(
      operation.parameterNames,
      operation.bindingKind,
      operation.categories,
      availableParameters,
      operation.parameterTypeHints,
    ).length === 0;
  });
}

export function collectConnectorBindingParameters(config: {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
}): string[] {
  return sanitizeRevitParameterNames([
    ...collectPipeConnectorBindingParameters(config),
    ...collectNonPipeConnectorBindingParameters(config),
  ]);
}

export const PIPE_CATEGORIES = [
  'Pipes',
  'Flex Pipes',
  'Pipe Fittings',
  'Pipe Accessories',
  'FlexPipes',
  'PipeFittings',
  'PipeAccessories',
  'Generic Models', // ONLY for OBJECT-TYPE parameter
  'Mechanical Equipment', // ONLY for OBJECT-TYPE parameter
];

export const DEFAULT_PROJECT_PARAMETER_BINDING_CATEGORIES = [
  ...CONNECTOR_PARAMETER_CATEGORIES,
];

export const MAPPED_PARAMETER_PROJECT_CATEGORIES = [
  ...CONNECTOR_PARAMETER_CATEGORIES,
];

export function resolveProjectParameterBindingCategories(
  categories?: string[],
): string[] {
  if (Array.isArray(categories) && categories.length > 0) {
    return categories;
  }

  return DEFAULT_PROJECT_PARAMETER_BINDING_CATEGORIES;
}
